import { FC, useCallback, useMemo } from 'react';

import { useRequestServiceAccessMutation } from 'store/serviceService';
import { useTranslations } from 'hooks/useTranslations';
import { ServiceAccess, ServiceAttributes } from 'models/service';
import Button from 'components/UI/Buttons/Button';
import Heading, { Type } from 'components/UI/Heading';

import {
  Card,
  CardActions,
  CardImageContainer,
  CardGrid,
  CardContent
} from './styled';
import Loader from '../Loader';

type Props = {
  service: ServiceAttributes;
  isService: { [key in ServiceAccess]?: boolean };
};

export const ServiceCard: FC<Props> = ({ service, isService }) => {
  const t = useTranslations();

  const [requestAccess, { isLoading }] = useRequestServiceAccessMutation();

  // Handle button click
  const onClick = useCallback(() => {
    if (isService.approved) {
      window.open(service.serviceUrl);
    } else if (isService.available) {
      requestAccess({ iamGroup: service.iamGroup });
    }
  }, [isService, service, requestAccess]);

  // Render button or status text
  const button = useMemo(() => {
    if (isLoading) {
      return <Loader size="small" />;
    }

    return (
      <Button
        color={isService.approved ? 'primary' : 'secondary'}
        onClick={onClick}
        disabled={isLoading || isService.pending}
      >
        {t(
          isService.approved
            ? 'myservices.btn_approved'
            : isService.pending
              ? 'myservices.btn_pending'
              : 'myservices.btn_available'
        )}
      </Button>
    );
  }, [isService, onClick, t, isLoading]);

  return (
    <Card data-testid="service-card">
      <CardImageContainer>
        <img src={service.imageUrl} alt={service.name} />
      </CardImageContainer>
      <CardGrid>
        <CardContent>
          <Heading type={Type.h2}>{service.name}</Heading>
          <p>{service.description}</p>
        </CardContent>
        <CardActions>{button}</CardActions>
      </CardGrid>
    </Card>
  );
};
